/**
 * =========================================================================================
 *   Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *   Module       : components/errors/BackupErrorsArea
 *   Feature      : 252 - Backup Error Visibility & Guided Remediation
 *   Description  : The Backup Errors screen (FR-001) — the ACTUAL errors, not counts.
 *
 *                  Defaults chosen deliberately:
 *                    • grouped by root cause (FR-010) — one paused capacity failing 40 backups
 *                      must read as ONE action, not 40 rows;
 *                    • status = failing + at_risk (FR-001a) — what needs attention now.
 *
 *                  Reads ?errorKey / ?code / ?entity on mount so an ALERT deep-link lands on the
 *                  detail rather than a dead end (FR-008).
 *   Author       : Peter Dobler (Dobler Data Solutions)
 * =========================================================================================
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  Card, DataTable, DataTableColumn, Badge, EmptyState, ErrorState, LoadingState,
  Select, Input, Button, PaginationControls,
} from '@/components/ui';
import { formatEastern } from '@/utils/dateFormatter';
import backupErrorsApi, { BackupErrorItem, ErrorGroup, ErrorListResponse } from '../../api/backupErrors';
import { CauseClassBadge, StatusBadge, CAUSE_META } from './CauseClassBadge';
import useBackupErrorDrawer from '../../store/backupErrorStore';

/**
 * 🛑 Every status, spelled out. NEVER '' for "all".
 *
 * The empty string looked like a natural "no filter", but it travelled as: '' is falsy in
 * BackupErrorsArea -> the param is omitted -> the route's `csv(status) ?? ['failing','at_risk']`
 * DEFAULTS. So "Everything" issued the identical query to "Needs attention" (18 rows instead of
 * 258), and the alert deep-link's setStatus('') — which exists precisely so an alert never hides
 * the error it points at — silently hid any RESOLVED error an alert linked to.
 * An explicit list cannot be reinterpreted downstream.
 */
/**
 * 🛑 RESOLVED IS DELIBERATELY NOT HERE.
 *
 * A resolved error is one a LATER SUCCESSFUL RUN already fixed. In prod that is 210 of 260
 * problems — so "everything" was 92% history, and the screen invited an operator to work through
 * remediation for things that corrected themselves days or weeks ago. Owner verdict during the
 * SC-004 walkthrough: "we are chasing ghosts... remove the errors that are not relevant".
 *
 * The DATA is untouched — vw_backup_errors still surfaces resolved rows, and the SC-001 surface
 * oracle and any attestation query still measure against them. This is a presentation decision:
 * the Errors screen shows what is still true. `status=resolved` remains reachable by URL for those
 * queries; it is simply not something the UI steers anyone into.
 *
 * Accepted limits STAY: 31 permanent, documented platform gaps. They are not work, but they are
 * the register of what cannot be protected, and they are badged as such rather than as failures.
 */
const ALL_STATUSES = 'failing,at_risk,accepted_limitation';

const SURFACES = [
  'backup_run', 'warehouse_backup_run', 'definition_backup', 'definition_artifact',
  'restore_run', 'backup_copy', 'retention', 'verification', 'readiness',
];

export const BackupErrorsArea: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const openDrawer = useBackupErrorDrawer((s) => s.open);

  const [data, setData] = useState<ErrorListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // filters
  const [grouped, setGrouped] = useState(true);
  const [causeClass, setCauseClass] = useState('');
  const [surface, setSurface] = useState('');
  const [status, setStatus] = useState('failing,at_risk');
  const [q, setQ] = useState('');
  /** Exact entity scope from an alert deep-link (?entityId=). Separate from `q` on purpose. */
  const [entityId, setEntityId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      setData(await backupErrorsApi.list({
        group: grouped ? 'cause' : 'none',
        causeClass: causeClass ? [causeClass] : undefined,
        surface: surface ? [surface] : undefined,
        status: status ? status.split(',') : undefined,
        entityId: entityId ? [entityId] : undefined,
        q: q || undefined,
        page, pageSize,
      }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load backup errors');
    } finally { setLoading(false); }
  }, [grouped, causeClass, surface, status, entityId, q, page, pageSize]);

  useEffect(() => { void load(); }, [load]);

  /**
   * FR-008 — land an alert deep-link on something useful, never a dead end:
   *   ?errorKey=  → open that exact error's detail drawer
   *   ?entityId=  → scope the list to that entity by EXACT id (alertSourceLink sends this; the
   *                 alert knows the entity, not the error key)
   *   ?entity=    → legacy alias, still honoured so older links/bookmarks are not broken
   *   ?code=      → narrow to that error code
   *
   * 🛑 ?entityId= must NOT be routed into `q`. `q` is a LIKE over entity_name / raw_detail /
   * stored_code, and an entity GUID matches none of those — which is exactly why every backup
   * alert used to land on the "No backup errors" empty state.
   */
  useEffect(() => {
    const key = params.get('errorKey');
    const entityIdParam = params.get('entityId') ?? params.get('entity');
    const code = params.get('code');
    if (!key && !entityIdParam && !code) return;

    if (key) openDrawer(key);
    if (entityIdParam || code) {
      if (entityIdParam) setEntityId(entityIdParam);
      if (code) setQ(code);
      setGrouped(false);      // a deep-link should show the matching rows, not a collapsed group
      setStatus(ALL_STATUSES); // never hide the very error the alert is about — including a resolved one
      setPage(1);
    }
    const next = new URLSearchParams(params);
    ['errorKey', 'entityId', 'entity', 'code'].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
  }, [params, openDrawer, setParams]);

  const columns = useMemo<DataTableColumn<BackupErrorItem>[]>(() => [
    {
      key: 'entityName', header: 'Entity',
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate text-[var(--color-text-primary)]">{r.entityName ?? '—'}</div>
          <div className="truncate text-xs text-[var(--color-text-secondary)]">
            {r.entityType ?? '—'}{r.objectName ? ` · ${r.objectName}` : ''}
          </div>
        </div>
      ),
    },
    { key: 'surface', header: 'Surface', render: (r) => <span className="text-xs">{r.surface}</span> },
    { key: 'causeClass', header: 'Cause', render: (r) => <CauseClassBadge causeClass={r.causeClass} /> },
    {
      key: 'title', header: 'Reason', wrap: true,
      render: (r) => (
        <div className="min-w-0">
          <div className="text-[var(--color-text-primary)]">{r.title}</div>
          {r.detailPreview && (
            <div className="truncate text-xs text-[var(--color-text-secondary)]">{r.detailPreview}</div>
          )}
        </div>
      ),
    },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    {
      key: 'attemptCount', header: 'Occurrences', align: 'right',
      render: (r) => <span className="tabular-nums">{r.attemptCount}</span>,
    },
    {
      key: 'lastSeen', header: 'Last seen', sortValue: (r) => r.lastSeen ?? '',
      render: (r) => <span className="text-xs">{r.lastSeen ? formatEastern(r.lastSeen) : '—'}</span>,
    },
  ], []);

  const toggle = (key: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="space-y-4">
      {/* filters (FR-009) */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[14rem] flex-1">
            <Input
              label="Search"
              placeholder="Entity, error text, or code…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
            />
          </div>
          <Select
            label="Cause"
            value={causeClass}
            onChange={(e) => { setCauseClass(e.target.value); setPage(1); }}
            options={[{ value: '', label: 'All causes' },
              ...Object.entries(CAUSE_META).map(([k, v]) => ({ value: k, label: v.label }))]}
          />
          <Select
            label="Surface"
            value={surface}
            onChange={(e) => { setSurface(e.target.value); setPage(1); }}
            options={[{ value: '', label: 'All surfaces' }, ...SURFACES.map((s) => ({ value: s, label: s }))]}
          />
          <Select
            label="Status"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            options={[
              { value: 'failing,at_risk', label: 'Needs attention' },
              { value: 'failing', label: 'Failing only' },
              { value: 'at_risk', label: 'At risk only' },
              { value: 'accepted_limitation', label: 'Accepted limits' },
              { value: ALL_STATUSES, label: 'Everything' },
            ]}
          />
          <Button variant="secondary" onClick={() => setGrouped((g) => !g)}>
            {grouped ? 'Show flat list' : 'Group by cause'}
          </Button>
        </div>

        {/* An entity scope arrives from an alert deep-link and is otherwise INVISIBLE — the user
            would see a short list with no idea why, and no way back. Always show it, always
            offer the way out. */}
        {entityId && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[var(--color-border)] pt-3">
            <span className="text-sm text-[var(--color-text-secondary)]">
              Showing errors for one entity
              {data?.items?.[0]?.entityName ? ` — ${data.items[0].entityName}` : ''}
            </span>
            <Badge variant="info" title={entityId}>{entityId.slice(0, 8)}…</Badge>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setEntityId(''); setStatus('failing,at_risk'); setGrouped(true); setPage(1); }}
            >
              Show all errors
            </Button>
          </div>
        )}
      </Card>

      {loading && <LoadingState />}
      {!loading && err && <ErrorState message={err} onRetry={() => void load()} />}

      {!loading && !err && data && data.items.length === 0 && (
        <EmptyState
          title="No backup errors"
          message="Nothing matches these filters. Errors that a later successful run already fixed are not shown here — this screen lists what is still true."
        />
      )}

      {!loading && !err && data && data.items.length > 0 && (
        <>
          {grouped && data.groups ? (
            <div className="space-y-2">
              {data.groups.map((g: ErrorGroup) => {
                const key = `${g.causeClass}::${g.code}`;
                const isOpen = expanded.has(key);
                return (
                  <Card key={key} padding="none">
                    <button
                      type="button"
                      onClick={() => toggle(key)}
                      className="flex w-full items-center gap-3 p-3 text-left"
                      aria-expanded={isOpen}
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
                      <CauseClassBadge causeClass={g.causeClass} />
                      <span className="min-w-0 flex-1 truncate text-[var(--color-text-primary)]">{g.title}</span>
                      {g.isAcceptedLimitation && <Badge variant="info">Accepted limit</Badge>}
                      <Badge variant="default" title="Entities affected by this one root cause">
                        {g.affectedCount} affected
                      </Badge>
                    </button>
                    {isOpen && (
                      <ul className="border-t border-[var(--color-border)]">
                        {g.entities.map((e) => {
                          // 🛑 Two rows in one group can share a display name and still be DIFFERENT
                          // problems: either two entities that happen to be named the same (prod has
                          // two warehouses both called claims_warehouse) or one entity failing on two
                          // surfaces. Without a disambiguator they read as an accidental duplicate.
                          const sameName = g.entities.filter(
                            (o) => (o.entityName ?? '') === (e.entityName ?? ''),
                          ).length > 1;
                          return (
                          <li key={e.errorKey}>
                            <button
                              type="button"
                              onClick={() => openDrawer(e.errorKey, e.runId)}
                              className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left hover:bg-[var(--color-bg-subtle)]"
                            >
                              <span className="flex min-w-0 flex-1 items-center gap-2">
                                <span className="truncate text-sm text-[var(--color-text-primary)]">
                                  {e.entityName ?? e.entityId ?? '—'}
                                </span>
                                {sameName && e.entityId && (
                                  <span
                                    className="shrink-0 font-mono text-xs text-[var(--color-text-secondary)]"
                                    title={e.entityId}
                                  >
                                    {e.entityId.slice(0, 8)}
                                  </span>
                                )}
                                <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">
                                  {e.surface}
                                </span>
                              </span>
                              <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">
                                {e.lastSeen ? formatEastern(e.lastSeen) : '—'}
                              </span>
                            </button>
                          </li>
                          );
                        })}
                      </ul>
                    )}
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card padding="none">
              <DataTable<BackupErrorItem>
                columns={columns}
                rows={data.items}
                rowKey={(r) => r.errorKey}
                onRowClick={(r) => openDrawer(r.errorKey, r.runId)}
                showPagination={false}
                minWidth="70rem"
              />
            </Card>
          )}

          <PaginationControls
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            totalPages={totalPages}
            start={(data.page - 1) * data.pageSize}
            shown={data.items.length}
            onPage={setPage}
            onPageSize={(s: number) => { setPageSize(s); setPage(1); }}
          />
        </>
      )}
    </div>
  );
};

export default BackupErrorsArea;
